import { CaseStateMapper } from '@core/mapper/caseStateMapper';
import { ValidCaseState } from '@core/model/domain/caseState';
import { Bad } from '@core/model/framework/Result/BadResult';
import { Ok, OkVal } from '@core/model/framework/Result/OkResult';
import { Result } from '@core/model/framework/Result/Result';
import { SignalR } from '@core/model/framework/notifications/signalR';
import { WaitOnCaseStateNotificationGateway } from '@core/model/gateway/waitOnCaseStateNotificationGateway';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';

type WaitOnCaseStateNotificationGatewayOutputViaSignalR = Promise<
  OkVal<ValidCaseState> | Bad<'CASE_STILL_IN_PROGRESS'>
>;

@injectable()
export class WaitOnCaseStateNotificationViaSignalR implements WaitOnCaseStateNotificationGateway {
  private topicName = 'OPEN_BANKING_CASE_CLERK_STATUS_UPDATED' as const;

  constructor(
    @inject(SERVICE_ID.signalR) private readonly signalR: SignalR,
    @inject(CaseStateMapper) private readonly caseStateMapper: CaseStateMapper,
    @inject(SERVICE_ID.waitOnCaseStateTimeoutInSeconds)
    private readonly timeoutInSeconds: number
  ) {}

  async awaitCaseStateUpdate(): WaitOnCaseStateNotificationGatewayOutputViaSignalR {
    const clerkResult = await this.signalR.waitOnSingleNotification(this.topicName, this.timeoutInSeconds);

    if (clerkResult.success === false) {
      return Result.matchBad(clerkResult, {
        NOTIFICATION_TIMED_OUT: () => Bad('CASE_STILL_IN_PROGRESS'),
      });
    }

    return Ok(this.caseStateMapper.toDomain(clerkResult.value.status));
  }

  stopWaitingCaseStateUpdate() {
    this.signalR.disposeHandler(this.topicName);
  }
}
